import { Component, AfterViewInit, OnDestroy, Input, SimpleChanges, EventEmitter, Output } from '@angular/core';
import * as L from 'leaflet';
import { ParcDetailsDto, ParcLimitropheDto } from 'src/api/models';
import { LoaderService } from '../../loader/loader.service';
import { MapScreenType } from 'src/core/models/map-screen-type';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-map',
  templateUrl: './open-street-map.component.html',
  styleUrls: ['./open-street-map.component.css']
})
export class OpenStreetMapComponent implements AfterViewInit, OnDestroy {
  @Input() height: number = 250;
  @Input() latitude: number = 50.4669; 
  @Input() longitude: number = 4.8674;
  @Input() pointData: any;
  @Input() nbrVisits: number;
  @Input() zoom: number = 10;
  @Input() type: MapScreenType = MapScreenType.Parcs;
  @Input() points: any[] = [];
  @Output() markerClicked = new EventEmitter<any>();

  private map: L.Map | undefined;
  private markersLayer: L.LayerGroup = L.layerGroup();
  private mapInitialized = false;

  constructor(
    private translateService: TranslateService,
    private loaderService: LoaderService)
  {
    this.loaderService.Load();
  } 

  ngAfterViewInit(): void {
    this.tryInitializeMap();
  }

  private tryInitializeMap() {
    const hasData = this.points && this.points.length > 0;
  
    if (!this.mapInitialized && hasData) {
      this.loaderService.FinishLoad();
      setTimeout(() => {
        const mapElement = document.getElementById('map');
        if (!mapElement) {
          console.warn('Map container still not found. Retrying...');
          return;
        }
  
        this.initMap();
        this.mapInitialized = true;
      }, 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.tryInitializeMap();
  }

  ngOnDestroy(): void {
    this.map?.remove();
    this.mapInitialized = false;
  }

  isSmallWidth() {
    return window.innerWidth < 1200;
  }

  getHeight() {
    return { 'height': this.height+'vh' };
  }

  initMap(): void {
    if (this.map) {
      this.map.remove();
    }

    this.map = L.map('map', {
      center: [this.latitude, this.longitude], 
      zoom: this.zoom,
      layers: [
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap'
        })
      ]
    });

    this.markersLayer.clearLayers();

    const dataToShow = [...this.points]; 

    if(this.pointData != null)
    {
      dataToShow.push(this.pointData);
    }
    if(dataToShow != null) {
      dataToShow.forEach(p => {
        if(p.Latitude!! && p.Longitude!!) {
          const icon = L.icon({
            iconUrl: this.getIcon(p),
            iconSize: [30, 40],
            iconAnchor: [15, 40],
            popupAnchor: [0, -35]
          });
  
          const marker = L.marker([p.Latitude, p.Longitude], { icon });

          let visitsOfDayText = this.translateService.instant('Map.VisitsOfDay');
          let detailText = this.translateService.instant('Map.Details');
  
          let popupContent = "";
          switch(this.type) {
            case MapScreenType.Parcs:
              popupContent = this.latitude == p.Latitude && this.longitude == p.Longitude ?
                `<p>
                  <strong>${p.Identifiant}</strong><br/>
                  ${visitsOfDayText}: ${this.nbrVisits || 0}<br/>
                  <a href="/parcs/carte/${p.Id}">${detailText}</a>
                </p>`
                :
                `<p>
                  <strong>${p.Identifiant}</strong><br/>
                  ${visitsOfDayText}: ${p.VisitesDuJour || 0}<br/>
                  <a href="/parcs/carte/${p.Id}">${detailText}</a>
                </p>`;
              break;
            case MapScreenType.ParcDetail:
              popupContent = this.latitude == p.Latitude && this.longitude == p.Longitude ?
              `<p>
                <strong>${p.Identifiant}</strong><br/>
                ${visitsOfDayText}: ${this.nbrVisits || 0}<br/>
              </p>`
              :`<p>
                  <strong>${p.ParcDestination}</strong><br/>
                  <a href="/parcs/carte/${p.ParcDestinationId}">${detailText}</a>
                </p>`;
              break;
            case MapScreenType.Exutoires:
              break;
            case MapScreenType.Transporteurs:
              break;
          }
  
          if(popupContent.length > 0) {
            marker.bindPopup(popupContent);
          }
    
          marker.on('click', () => {
            this.clickedMarker(p);
          });
    
          this.markersLayer.addLayer(marker);
        }
      });
    }
    
    this.markersLayer.addTo(this.map);

    const group = L.featureGroup(this.markersLayer.getLayers() as L.Marker[]);
    if (dataToShow != null && dataToShow.length > 1) { 
      this.map.fitBounds(group.getBounds().pad(0.2));
    }
  }

  clickedMarker(p: any) {
    this.markerClicked.emit(p);
  } 

  getIcon(p: any) {
    switch(this.type) {
      case MapScreenType.Parcs:
        return this.latitude == p.Latitude && this.longitude == p.Longitude ? this.parcIcon(p) : this.nearParcIcon(p);
      case MapScreenType.ParcDetail:
        return this.latitude == p.Latitude && this.longitude == p.Longitude ? this.parcIcon(p) : this.nearParcIcon(p);
      case MapScreenType.Exutoires:
        if (p.EstInterneIC) 
          {
            return './assets/img/map-icon-blue.png';
          } 
          else 
          {
            return './assets/img/map-icon-green.png';
          }
      case MapScreenType.Transporteurs: 
        if (p.Type == "Transporteur" || p.Type == null) 
          {
            return './assets/img/map-icon-green.png';
          } 
          else 
          {
            return './assets/img/map-icon-blue.png';
          }
    }
  }

  parcIcon(p: ParcDetailsDto) {
    if (p.EstEnFermetureTemporaire) return './assets/img/map-icon-orange.png';
    if (p.EstOuvert) return './assets/img/map-icon-green.png';
    return './assets/img/map-icon-red.png';
  }
  
  nearParcIcon(p: ParcLimitropheDto) {
    if (p.EstEnFermetureTemporaire) return './assets/img/map-icon-orange1.png';
    if (p.EstOuvert) return './assets/img/map-icon-green1.png';
    return './assets/img/map-icon-red1.png';
  }
}
